import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  Target,
  Clock,
  Users,
  DollarSign,
  BarChart3,
  PieChart,
  RefreshCw } from
'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell } from
'recharts';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const COLORS = ['#14b8a6', '#f97316', '#3b82f6', '#eab308', '#8b5cf6', '#ec4899'];

export const CRMReports = () => {const { t } = useTranslation("crm");
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('ytd');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/crm/reports/pipeline`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch report data');
      const data = await response.json();
      setReportData(data);
    } catch (err) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  const pipeline = reportData?.pipeline || [];
  const won = reportData?.won || { count: 0, value: 0 };
  const lost = reportData?.lost || { count: 0, value: 0 };
  const totalPipelineValue = reportData?.total_pipeline_value || 0;
  const totalWeightedValue = reportData?.total_weighted_value || 0;

  // Mock KPI data (would come from API in production)
  const kpis = {
    leadConversion: { current: 43.6, target: 50 },
    avgSalesCycle: { current: 45, target: 40 },
    winRate: { current: won.count > 0 ? (won.count / (won.count + lost.count) * 100).toFixed(1) : 0, target: 35 },
    avgDealSize: { current: pipeline.length > 0 ? Math.round(totalPipelineValue / pipeline.reduce((sum, p) => sum + p.count, 0)) : 0, target: 75000 },
    customerRetention: { current: 92, target: 95 }
  };

  return (
    <div className="space-y-6" data-testid="crm-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("crmReports", "CRM Reports")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("salesAnalyticsAnd", "Sales analytics and performance metrics")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReportData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            {period === 'ytd' ? 'Year to Date' : period === 'q1' ? 'Q1 2024' : 'Last Month'}
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("overview", "Overview")}</TabsTrigger>
          <TabsTrigger value="pipeline">{t("pipeline", "Pipeline")}</TabsTrigger>
          <TabsTrigger value="team">{t("teamPerformance", "Team Performance")}</TabsTrigger>
          <TabsTrigger value="leads">{t("leadAnalytics", "Lead Analytics")}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* KPI Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Card className="card-elevated border-l-4 border-l-teal-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("leadConversion", "Lead Conversion")}</p>
                    <p className="text-2xl font-bold">{kpis.leadConversion.current}%</p>
                    <p className="text-xs text-muted-foreground">{t("target", "Target:")}{kpis.leadConversion.target}%</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-teal-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-elevated border-l-4 border-l-orange-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("avgSalesCycle", "Avg Sales Cycle")}</p>
                    <p className="text-2xl font-bold">{kpis.avgSalesCycle.current}{t("days", "days")}</p>
                    <p className="text-xs text-muted-foreground">{t("target", "Target:")}{kpis.avgSalesCycle.target}{t("days", "days")}</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-elevated border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("winRate", "Win Rate")}</p>
                    <p className="text-2xl font-bold">{kpis.winRate.current}%</p>
                    <p className="text-xs text-muted-foreground">{t("target", "Target:")}{kpis.winRate.target}%</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Target className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-elevated border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("avgDealSize", "Avg Deal Size")}</p>
                    <p className="text-2xl font-bold">${(kpis.avgDealSize.current / 1000).toFixed(1)}k</p>
                    <p className="text-xs text-muted-foreground">{t("target", "Target: $")}{(kpis.avgDealSize.target / 1000).toFixed(0)}k</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-elevated border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("retention", "Retention")}</p>
                    <p className="text-2xl font-bold">{kpis.customerRetention.current}%</p>
                    <p className="text-xs text-muted-foreground">{t("target", "Target:")}{kpis.customerRetention.target}%</p>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-muted-foreground" />{t("pipelineByStage", "Pipeline by Stage")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pipeline.map((stage, index) =>
                  <div key={stage.stage}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{stage.stage}</span>
                        <span className="font-medium">${(stage.value / 1000).toFixed(0)}{t("k", "k (")}{stage.count}{t("deals", "deals)")}</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div
                        className="h-full rounded-full"
                        style={{
                          width: `${totalPipelineValue > 0 ? stage.value / totalPipelineValue * 100 : 0}%`,
                          backgroundColor: COLORS[index % COLORS.length]
                        }} />
                      
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-muted-foreground" />{t("wonVsLost", "Won vs Lost")}

                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPie>
                    <Pie
                      data={[
                      { name: 'Won', value: won.value, count: won.count },
                      { name: 'Lost', value: lost.value, count: lost.count }]
                      }
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value">
                      
                      <Cell fill="#22c55e" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name, props) => [
                      `$${value.toLocaleString()} (${props.payload.count} deals)`,
                      name]
                      } />
                    
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-sm">{t("won", "Won: $")}{(won.value / 1000).toFixed(0)}k</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-sm">{t("lost", "Lost: $")}{(lost.value / 1000).toFixed(0)}k</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Stats */}
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-muted-foreground" />{t("pipelineSummary", "Pipeline Summary")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-teal-600">${(totalPipelineValue / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-muted-foreground">{t("totalPipeline", "Total Pipeline")}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-blue-600">${(totalWeightedValue / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-muted-foreground">{t("weightedValue", "Weighted Value")}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-green-600">${(won.value / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-muted-foreground">{t("won", "Won (")}{won.count})</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-3xl font-bold text-red-600">${(lost.value / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-muted-foreground">{t("lost", "Lost (")}{lost.count})</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>{t("pipelineAnalysis", "Pipeline Analysis")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="py-12 text-center text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-4" />
                <p>{t("detailedPipelineStage", "Detailed pipeline stage analysis and conversion metrics")}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>{t("teamPerformanceDetails", "Team Performance Details")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="py-12 text-center text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4" />
                <p>{t("individualPerformanceMetrics", "Individual performance metrics, activity tracking, and quota progress")}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leads Tab */}
        <TabsContent value="leads" className="space-y-6">
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>{t("leadAnalytics", "Lead Analytics")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="py-12 text-center text-muted-foreground">
                <Target className="h-12 w-12 mx-auto mb-4" />
                <p>{t("leadSourceAnalysis", "Lead source analysis, conversion funnels, and scoring insights")}</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};
